<script setup lang="ts">
import { useAppIndexStore } from '@base/stores/indexStore'
import type * as pfWilo from '@amirada/lib-wilo-shared'
import { type ExtCmd, ExtCmdType, PARAM } from '@amirada/lib-wilo-shared'
import { ut_buildParamData, ut_getText, ut_isScreenSize } from '@base/utils/utils'
import { ut_wilo_getSpaixDefaultUnits, ut_wilo_modifySpaixUnitsForRequest } from '~/utils/utils'
import type { RadioGroupItem } from '@nuxt/ui'
import { usePfinderAPI } from '~/composables/pfinderAPI'
import { useMaskStore } from '@base/stores/maskStore'
import type * as tp from '@base/utils/types'
import { EScreenSize } from '@base/utils/constants'

const p = defineProps<{
  appType: string
  realCountry: string
  countryLanguage: string
  isInternal: boolean
}>()

const indexStore = useAppIndexStore()
const pfinderAPI = usePfinderAPI()
const dutyPointInput: Ref<pfWilo.IDutyPointInput> = ref({})
const compactView: Ref<boolean | undefined> = ref(undefined)

const showAssistance: Ref<boolean> = ref(false)
const isUs: boolean = indexStore.state.paramData.rcco === 'us'
const noResult: Ref<boolean> = ref(false)
// Spaix errors of the last search. Kept apart from noResult on purpose — see searchQS.
const spaixErrors: Ref<pfWilo.TErrorEntry[]> = ref([])

const meterFeet: number = 3.28084
const squareMeterFeet: number = 10.7639
const paMpsiFt: number = 0.02262059
const KtoF: number = 1.8
const m3hUsgpm: number = 4.402881245487

const heatCalcInputAr: Ref<number[]> = !isUs ? ref([10, 10, 3]) : ref([30, 30, 3])
const heatCalcInputTitlesAr: string[] = ['width', 'length', 'storeys']
const heatCalcInputUnitsAr: string[] = !isUs ? ['m', 'm', ''] : ['ft', 'ft', '']
const heatCalcArea: Ref<number> = !isUs ? ref(300) : ref(2700)
const heatCalcRangesAr: Ref<number[]> = indexStore.state.paramData.rcco !== 'cn' ? !isUs ? ref([100, 1995, 15]) : ref([2.5, 1995, 30]) : ref([200, 1995, 10])
const heatCalcRangesMinMaxAr: Ref<string[]> = !isUs ? ref(['50|200', '1970|2022', '5|20']) : ref(['1|5', '1970|2022', '10|40'])
const heatCalcRangesTitlesAr: string[] = ['pipe-resistance', 'year-construction', 'temperature-difference']
const heatCalcRangesUnitsAr: string[] = !isUs ? ['Pa/m', '', 'K'] : ['psi/ft', '', 'F']
const heatCalcSpecificQ: string = '|160|160|150|150|150|130|130|130|115|115|115|115|115|115|100|100|100|100|100|100|90|90|90|80|80|70|70|70|60|60|50|50|45|45|45|45|45|45|40|40|40|40|40|40|40|40|40|40|40|40|40|40|40|'
const heatCalcRadioAr: Ref<number[]> = ref([0, 0])
const heatCalcRadioTitlesAr: string[] = ['heat-meter', 'heating-type']
const heatCalcRadioValuesAr: string[] = ['without-heat-meter|with-heat-meter', 'radiator-heating|underfloor-heating']

const waterCalcInputAr: Ref<number[]> = !isUs ? ref([15, 30]) : ref([50, 100])
const waterCalcInputTitlesAr: string[] = ['length-pipe-circulation', 'length-pipe-total']
const waterCalcInputUnitsAr: string[] = !isUs ? ['m', 'm', ''] : ['ft', 'ft', '']
const waterCalcRangesAr: Ref<number[]> = !isUs ? ref([200, 4]) : ref([5, 7])
const waterCalcRangesMinMaxAr: string[] = !isUs ? ['50|500', '2|5'] : ['1|12', '1|10']
const waterCalcRangesTitlesAr: string[] = ['pipe-resistance', 'temperature-difference']
const waterCalcRangesUnitsAr: string[] = !isUs ? ['Pa/m', 'K'] : ['psi/ft', 'F']

const tabHeating: string = '8ae2819c8fdbe552018fdde8db0c0edf'
const tabWater: string = '8ae2819c8fdbe552018fdde913ef0594'
const tableNode: Ref<pfWilo.TableNode | undefined> = ref()
const isLoading: Ref<boolean> = ref(false)

// The QS filter runs without URL params, so the selection lives here instead of in the route.
const localPsx: Ref<string> = ref('')
// The quick-select universe from the server (meta.psn): node ids of every product Spaix returned a
// curve for, WITHOUT the filter applied. Passed back as `psn` so the mask's hit counts and total
// count the Spaix intersection instead of the whole catalog.
const localPsn: Ref<number[] | undefined> = ref(undefined)
// True while a NEW duty point is being searched. The mask is taken off the page for that time, so it
// comes back the way it looks on a fresh visit (see setDutyPoint). Not set for a filter search —
// there the mask must stay put, panel and all.
const dutyPointSearch: Ref<boolean> = ref(false)
// Duty point the current result was fetched for — re-used when only the filter changes.
const searchDutyPoint: Ref<pfWilo.IDutyPointInput | undefined> = ref()
// Config holding the quick-select products and their filter mask.
const objPath: ComputedRef<string> = computed(() => indexStore.state.currentNode.namePath + '/' + p.appType + '_products')

onBeforeMount(() => {
  // Dutypoint in URL
  if (indexStore.state.paramData[PARAM.spaixSettings]) {
    dutyPointInput.value = JSON.parse(indexStore.state.paramData[PARAM.spaixSettings])
  }
})

// Return UI array for forms
function getFormItems(obj: string | undefined): RadioGroupItem[] {
  const res: RadioGroupItem[] = []
  if (obj) {
    for (const [index, key] of obj.split('|').entries()) {
      res.push({
        label: ut_getText('txt_qs-' + key),
        value: index,
      })
    }
  }
  return res
}

function calculateQH(calcSize?: boolean): void {
  // resultTable = {};
  let calcQ: number = 0
  let calcH: number = 0

  if (p.appType === tabHeating) {
    if (heatCalcInputAr.value[0] && heatCalcInputAr.value[1] && heatCalcInputAr.value[2] && heatCalcRangesAr.value[0] && heatCalcRangesAr.value[1] && heatCalcRangesAr.value[2]) {
      for (const input in heatCalcInputAr.value) {
        heatCalcInputAr.value[input] = validNumber(heatCalcInputAr.value[input] || 0)
      }
      if (calcSize) {
        heatCalcArea.value = heatCalcInputAr.value[0] * heatCalcInputAr.value[1] * heatCalcInputAr.value[2]
      }
      const width: number = !isUs ? heatCalcInputAr.value[0] : heatCalcInputAr.value[0] / meterFeet
      const length: number = !isUs ? heatCalcInputAr.value[1] : heatCalcInputAr.value[1] / meterFeet
      const area: number = !isUs ? heatCalcArea.value : heatCalcArea.value / squareMeterFeet
      const pipeResistance: number = !isUs ? heatCalcRangesAr.value[0] : heatCalcRangesAr.value[0] / paMpsiFt
      const tempRange: number = !isUs ? heatCalcRangesAr.value[2] : heatCalcRangesAr.value[2] / KtoF

      // Q
      const specificQ: number = Number(heatCalcSpecificQ.split('|')[heatCalcRangesAr.value[1] - 1969])
      const neededKw: number = Number(area) * specificQ / 983.2
      calcQ = neededKw / (1.1625 * Number(tempRange))
      // H
      const maxPipeLength: number = (Number(width) + Number(length) + (Number(heatCalcInputAr.value[2]) * 3)) * 2
      calcH = (Number(pipeResistance) * maxPipeLength * 2.6) / (10000)
      if (Number(heatCalcRadioAr.value[0]) === 1) {
        calcH += 0.8
      }
    }
  }
  else if (p.appType === tabWater) {
    if (waterCalcInputAr.value[0] && waterCalcInputAr.value[1]) {
      for (const input in waterCalcInputAr.value) {
        waterCalcInputAr.value[input] = validNumber(waterCalcInputAr.value[input] || 0)
      }
      // Q
      const maxLength: number = !isUs ? Number(waterCalcInputAr.value[0]) : Number(waterCalcInputAr.value[0]) / meterFeet
      const sumLength: number = !isUs ? Number(waterCalcInputAr.value[1]) : Number(waterCalcInputAr.value[1]) / meterFeet
      const pipeResistance: number = !isUs ? Number(waterCalcRangesAr.value[0]) : Number(waterCalcRangesAr.value[0]) / paMpsiFt

      const tempVals: number[] = !isUs ? [2, 3, 4, 5] : [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
      const vSpezVals: number[] = !isUs ? [4.6, 3.1, 2.3, 1.8] : [8.8133, 6.8446, 5.3255, 4.1876, 3.3625, 2.7818, 2.3771, 2.08, 1.8221, 1.535]
      const vSpez: number = vSpezVals[tempVals.indexOf(Number(waterCalcRangesAr.value[1]))] || 0

      calcQ = (sumLength * vSpez) / 1000
      // H
      calcH = (pipeResistance * maxLength * 2.6) / 10000
    }
  }
  if (isUs) {
    calcQ = calcQ * m3hUsgpm
    calcH = calcH * meterFeet
  }

  dutyPointInput.value = {
    DutyPoint: {
      Head: +calcH.toFixed(2),
      Flow: +calcQ.toFixed(2),
    },
    SelectedUnits: ut_wilo_getSpaixDefaultUnits(p.realCountry),
  }
}

function changeHeatingType() {
  heatCalcRangesAr.value[0] = heatCalcRadioAr.value[1] === 0 ? 100 : 160
  heatCalcRangesAr.value[2] = heatCalcRadioAr.value[1] === 0 ? 15 : 8
  if (isUs) {
    heatCalcRangesAr.value[0] = heatCalcRangesAr.value[0] * paMpsiFt
    heatCalcRangesAr.value[2] = heatCalcRangesAr.value[2] * KtoF
  }
  calculateQH()
}

function rangeVal(index: number): string {
  let res: string = ''
  if (p.appType === tabHeating) {
    res = heatCalcRangesAr.value[index]?.toString() || ''
  }
  else if (p.appType === tabWater) {
    res = waterCalcRangesAr.value[index]?.toString() || ''
  }
  return res
}

function validNumber(input: number) {
  if (input < 0) {
    input = 0
  }
  if (input > 100000) {
    input = 100000
  }
  return input
}

// paramData the mask is stored/read under: the filter is local, so the selected psx is baked into
// queryUrlServer — that is exactly the cache key useMask() computes for noUrlParams mode.
function qsParamData(psx: string = localPsx.value, psn: number[] | undefined = localPsn.value): tp.IParamData {
  return ut_buildParamData(indexStore.state.paramData, { objPath: objPath.value, psx, psn })
}

// Fetch the quick-select table for the current duty point + filter, and the filter mask alongside it.
// useMask() fetches on its own now, so the explicit callMaskData calls below are not there to make
// the mask appear at all. They are there for the ORDER: both psx and psn are part of the mask cache
// key, and switching one before the mask for the target key is loaded leaves useMask() on an empty
// key for a tick — the mask unmounts and takes the open filter panel with it. So: load the target
// key, then switch. On the switch the composable's own fetch is a cache hit and no request follows.
async function searchQS(): Promise<void> {
  const data: pfWilo.IDutyPointInput | undefined = searchDutyPoint.value
  if (!data) { return }

  // No VSX for bots embParam6 — skipped silently, a log line per bot search was only noise (removed 13.10.2026)
  if (indexStore.state.paramData.embeddedInfo.embparam6 === 'true') {
    return
  }

  isLoading.value = true
  noResult.value = false
  spaixErrors.value = []
  // `tableNode` is deliberately NOT cleared here: it also gates the filter mask, and dropping it
  // would unmount the mask on every search — the filter panel would visibly disappear for the
  // duration of the call. The compare table is hidden via isLoading instead, so no stale table can
  // be mistaken for the new result, and every path that ends without a usable result clears
  // `tableNode` explicitly below.
  const args: ExtCmd<ExtCmdType.SPAIX_QS>['arg'] = {
    type: p.appType === tabHeating ? 'heating' : 'domestic_hot_water',
    dp: data.DutyPoint as pfWilo.TDutyPoint,
    units: ut_wilo_modifySpaixUnitsForRequest(data.SelectedUnits),
    psx: localPsx.value || undefined,
  }
  // Pass the same paramData: its queryUrlServer.psx is copied into the query params, so a psx there
  // can never contradict args.psx (the query params win in fetchData).
  const res = await pfinderAPI.fetchData(ExtCmdType.SPAIX_QS, args, { noLoading: true, paramData: qsParamData() })

  // Spaix could not deliver: the call itself was fine, only the result is missing, so the server
  // answers 200 and puts the reason in result.Errors — the same shape the direct spaix call uses.
  // This must NOT fall through to noResult: "spaix is down" and "no pump matches" look identical to
  // a user, and showing "nothing found" for an outage sends them looking for a duty point that is
  // not the problem. The message is shown verbatim so it can be passed on to Wilo.
  if (res?.result?.Errors?.length) {
    spaixErrors.value = res.result.Errors
    tableNode.value = undefined
    isLoading.value = false
    return
  }

  // Assign unconditionally: a call that came back empty must drop the previous result, otherwise the
  // old table would reappear once the loading flag clears — attached to a filter it does not belong to.
  tableNode.value = res?.result
  if (!res?.result?.nodes) {
    noResult.value = true
  }
  // Sequential, not parallel: the mask needs the quick-select universe (meta.psn) that this very
  // call just computed, so its hit counts and total reflect the Spaix intersection.
  // Load the mask under the target key BEFORE switching psn, for the same reason setPsx does it for
  // psx: both are part of the mask cache key, so switching first leaves useMask() on an empty key
  // for one tick and the mask unmounts.
  const nextPsn: number[] | undefined = res?.result?.meta?.psn
  await useMaskStore().callMaskData(qsParamData(localPsx.value, nextPsn))
  // Assign only on a real change. Every call brings a FRESH array, so an unguarded assignment would
  // hand useMask() a new reference on every filter click and make its watcher fire for a universe
  // that did not move — a no-op fetch on an unchanged cache key. Comparing the contents keeps this
  // right either way: the universe is expected to change only with the duty point, and if it ever
  // did change on a filter click, this would still notice.
  if ((nextPsn || []).join(',') !== (localPsn.value || []).join(',')) {
    localPsn.value = nextPsn
  }
  isLoading.value = false
}

// New duty point → drop the filter, a value of the previous result need not exist in the new one.
// The mask is taken off the page for the duration: anything carried over would belong to the
// previous duty point — an open filter panel, and the hit counts, which keep showing the old result
// because `tableNode` is deliberately not cleared. Simply rebuilding it is not enough, because the
// mask for the previous universe is still cached and would render again immediately, old numbers
// included. Gone and back means it returns collapsed and counts the new result, like a fresh visit.
async function setDutyPoint(data: pfWilo.IDutyPointInput): Promise<void> {
  searchDutyPoint.value = data
  localPsx.value = ''
  dutyPointSearch.value = true
  await searchQS()
  dutyPointSearch.value = false
}

// Filter changed inside the mask (local psx, no navigation) → re-run with the same duty point
async function setPsx(psx: string): Promise<void> {
  isLoading.value = true
  // Load the mask for the NEW psx BEFORE switching: the mask is keyed by psx, so switching first
  // would leave useMask() on an empty key for one tick — the mask would unmount and the filter
  // panel collapse. callMaskData is a no-op on a cache hit, so neither searchQS() below nor the
  // composable's own fetch on the switch produces a second request.
  await useMaskStore().callMaskData(qsParamData(psx))
  localPsx.value = psx
  await searchQS()
}
</script>

<template>
  <!-- Loading overlay div -->
  <div v-if="isLoading" class="h-full w-full left-0 top-0 fixed z-5000">
    <UIcon name="i-wilo-wilo-loading-curve" size="100px" class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-4000 animate-fade-in" />
  </div>

  <div class="bg-gunmetal-3 rounded-lg mt-8 p-8 overflow-x-auto">
    <!-- Dutypoint -->
    <div class="flex items-center">
      <UIcon name="i-wilo-wilo-pump" class="mr-4 size-7" />
      <h3 v-html="ut_getText('txt_qs-enter-dutypoint')" />
    </div>
    <div class="flex flex-wrap gap-x-16 gap-y-8 mt-6">
      <InputDutyPoint
        :real-country="realCountry"
        :set-url="true"
        :duty-point-input="dutyPointInput"
        :is-quick-select="true"
        @update:duty-point="setDutyPoint($event)"
      />
    </div>
    <!-- Assistance -->
    <UButton
      class="mt-8 ml-auto"
      :icon="showAssistance ? 'i-wilo-xmark-solid-full' : 'i-wilo-chevron-right-solid-full'"
      :label="ut_getText(showAssistance ? 'txt_qs-close-assistance' : 'txt_qs-calculation-assistance')"
      color="tertiary"
      variant="ghost"
      @click="void (!showAssistance ? [calculateQH(), showAssistance = true] : showAssistance = false)"
    />
    <UCollapsible v-model:open="showAssistance">
      <template #content>
        <hr class="mt-8">
        <div class="flex items-center mt-10 mb-6">
          <UIcon name="i-wilo-wilo-house" class="mr-4 size-6" />
          <h3 v-html="ut_getText('txt_qs-specify-building')" />
        </div>
        <!-- ************ Heating ************ -->
        <template v-if="appType===tabHeating">
          <!-- Number inputs -->
          <div class="flex flex-wrap gap-x-16 gap-y-8">
            <div v-for="(key, index) in heatCalcInputTitlesAr" :key="key">
              <label
                :for="heatCalcInputTitlesAr[index]"
                class="font-bold"
                :class="index !== 2 ? 'mb-1' : ''"
                v-html="ut_getText('txt_qs-' + heatCalcInputTitlesAr[index]) + (heatCalcInputUnitsAr[index] ? ' [' + heatCalcInputUnitsAr[index] + '] ' : '')"
              />
              <template v-if="index===2">
                <UTooltip :text="ut_getText('txt_qs-' + heatCalcInputTitlesAr[index] + '_info')">
                  <UIcon name="i-wilo-circle-info-solid-full" class="ml-2 mb-1 size-4" />
                </UTooltip>
              </template>
              <div class="mt-2">
                <UInput
                  v-model="heatCalcInputAr[index]"
                  type="number"
                  min="0"
                  max="10000"
                  step="1"
                  @change="calculateQH(true)"
                />
              </div>
            </div>
            <div>
              <label for="area" class="font-bold" v-html="ut_getText('txt_qs-heated-area') + ' [' + (!isUs ? 'm²' : 'ft²') + '] '" />
              <UTooltip :text="ut_getText('txt_qs-heated-area_info')">
                <UIcon name="i-wilo-circle-info-solid-full" class="ml-2 mb-1 size-4" />
              </UTooltip>
              <div class="mt-2">
                <UInput
                  v-model="heatCalcArea"
                  type="number"
                  min="0"
                  max="10000"
                  step="1"
                  @change="calculateQH()"
                />
              </div>
            </div>
          </div>
          <!-- Radio Buttons -->
          <div class="flex flex-wrap gap-x-16 gap-y-8 mt-7">
            <div v-for="(input, index) of heatCalcRadioTitlesAr" :key="input">
              <label :for="heatCalcRadioTitlesAr[index]" class="font-bold" v-html="ut_getText('txt_qs-' + heatCalcRadioTitlesAr[index])" />
              <UTooltip :text="ut_getText('txt_qs-' + input + '_info')">
                <UIcon name="i-wilo-circle-info-solid-full" class="ml-2 mb-1 size-4" />
              </UTooltip>
              <URadioGroup
                v-model="heatCalcRadioAr[index]"
                class="mt-2"
                :items="getFormItems(heatCalcRadioValuesAr[index])"
                @change="index === 0 ? calculateQH() : changeHeatingType()"
              />
            </div>
          </div>
          <!-- Sliders -->
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-16 gap-y-8 mt-8">
            <div v-for="(key, index) in heatCalcRangesTitlesAr" :key="key">
              <div>
                <label :for="heatCalcRangesTitlesAr[index]" class="font-bold" v-html="ut_getText('txt_qs-' + heatCalcRangesTitlesAr[index]) + ':'" />
                <b class="text-green ml-2" v-html="rangeVal(index) + ' ' + heatCalcRangesUnitsAr [index]" />
              </div>
              <USlider
                v-model="heatCalcRangesAr[index]"
                :min="Number(heatCalcRangesMinMaxAr[index]?.split('|')[0])"
                :max="Number(heatCalcRangesMinMaxAr[index]?.split('|')[1])"
                :step="(isUs && index === 0) ? 0.5 : 1"
                class="mt-4 px-1 pb-4"
                :ui="{ track: 'bg-gunmetal-2' }"
                @change="calculateQH()"
              />
              <div class="flex justify-between">
                <div v-html="(index ===1 ? '≤ ' : '') + heatCalcRangesMinMaxAr[index]?.split('|')[0] + ' ' + heatCalcRangesUnitsAr [index]" />
                <div v-html="(index ===1 ? '≥ ' : '') + heatCalcRangesMinMaxAr[index]?.split('|')[1] + ' ' + heatCalcRangesUnitsAr [index]" />
              </div>
            </div>
          </div>
        </template>

        <!-- ************ Domestic hot water ************ -->
        <template v-if="appType===tabWater">
          <!-- Number inputs -->
          <div class="flex flex-wrap gap-x-16 gap-y-8">
            <div v-for="(key, index) in waterCalcInputTitlesAr" :key="key">
              <label
                :for="waterCalcInputTitlesAr[index]"
                class="font-bold"
                :class="index !== 0 ? 'mb-1' : ''"
                v-html="ut_getText('txt_qs-' + waterCalcInputTitlesAr[index]) + (waterCalcInputUnitsAr[index] ? ' [' + waterCalcInputUnitsAr[index] + '] ' : '')"
              />
              <template v-if="index===0">
                <UTooltip :text="ut_getText('txt_qs-' + waterCalcInputTitlesAr[index] + '_info')">
                  <UIcon name="i-wilo-circle-info-solid-full" class="ml-2 mb-1 size-4" />
                </UTooltip>
              </template>
              <div class="mt-2">
                <UInput
                  v-model="waterCalcInputAr[index]"
                  type="number"
                  min="0"
                  step="1"
                  @change="calculateQH()"
                />
              </div>
            </div>
          </div>
          <!-- Sliders -->
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-16 gap-y-8 mt-8">
            <div v-for="(key, index) in waterCalcRangesTitlesAr" :key="key">
              <div>
                <label :for="waterCalcRangesTitlesAr[index]" class="font-bold" v-html="ut_getText('txt_qs-' + waterCalcRangesTitlesAr[index]) + ':'" />
                <b class="text-green ml-2" v-html="rangeVal(index) + ' ' + waterCalcRangesUnitsAr [index]" />
              </div>
              <USlider
                v-model="waterCalcRangesAr[index]"
                :min="Number(waterCalcRangesMinMaxAr[index]?.split('|')[0])"
                :max="Number(waterCalcRangesMinMaxAr[index]?.split('|')[1])"
                :step="(isUs && index === 0) ? 0.5 : 1"
                class="mt-2 px-1 pb-2"
                :ui="{ track: 'bg-gunmetal-2' }"
                @change="calculateQH()"
              />
              <div class="flex justify-between">
                <div v-html="waterCalcRangesMinMaxAr[index]?.split('|')[0] + ' ' + waterCalcRangesUnitsAr [index]" />
                <div v-html="waterCalcRangesMinMaxAr[index]?.split('|')[1] + ' ' + waterCalcRangesUnitsAr [index]" />
              </div>
            </div>
          </div>
        </template>
      </template>
    </UCollapsible>
  </div>

  <!-- Result -->
  <div v-if="tableNode && tableNode.meta && tableNode.nodes" class="mt-8">

    <LazyMask
      v-if="!dutyPointSearch"
      :obj-path="objPath"
      :no-url-params="true"
      :psx="localPsx"
      :psn="localPsn"
      :special-count="tableNode?.nodes?.length || 0"
      :country-language="countryLanguage"
      :real-country="realCountry"
      :is-historic="false"
      :is-quick-select="true"
      :compact-view="compactView !== undefined ? compactView : ut_isScreenSize([EScreenSize.sc_xs, EScreenSize.sc_sm, EScreenSize.sc_md])"
      class="my-8"
      @update:view="compactView = $event"
      @update:psx="setPsx($event)"
    />

    <!-- Hidden while a search runs: the mask above stays mounted so the filter panel does not
         disappear, but the table below belongs to the previous filter until the new result lands. -->
    <LazyCompare
      v-if="!isLoading"
      :table-node="tableNode"
      :country-language="countryLanguage"
      :real-country="realCountry"
      :is-internal="isInternal"
      :is-quick-select="true"
      :compact-view="compactView !== undefined ? compactView : ut_isScreenSize([EScreenSize.sc_xs, EScreenSize.sc_sm, EScreenSize.sc_md])"
      :vsx-service="tableNode.meta.vsxService"
      :session-id="tableNode.meta.sessionID"
      :ext-extra-values-ar="tableNode.meta.extraValues"
      :spaix-ids-ar="tableNode.meta.spaixIDs"
    />
  </div>

  <!-- Spaix error — same wording PumpCurve.vue uses for the direct spaix call, so a user sees the
       identical text no matter which of the two paths failed and can pass it on to Wilo. -->
  <div v-if="spaixErrors.length" class="mt-8">
    <template v-if="spaixErrors[0] && spaixErrors[0]['Code'] === 5">
      <div class="text-error" v-html="ut_getText('txt_vsx-invalid-geodetic')" />
    </template>
    <template v-else>
      <div v-for="(error, index) of spaixErrors" :key="index">
        <div class="text-error" v-html="JSON.stringify(error['MsgText']) + ' [Error:' + error['Error'] + ', Code:'+ error['Code'] + ']'" />
      </div>
    </template>
  </div>

  <!-- No Result -->
  <div v-if="noResult" class="mt-8">
    <div v-html="ut_getText('txt_qs-no-result')" />
    <UButton label="Wilo-Select" href="https://select.wilo.com" target="_blank" class="mt-4" />
  </div>
</template>
